// Bold the prefix of every content row directly under specific section
// headers, where "prefix" runs from offset 0 up to the first match of a
// per-header splitter regex.
//
// Group lifecycle:
//   - Header is strictly BOLD+UNDERLINE (the "real" section header).
//   - Group opens at a header whose text matches a config key.
//   - Group closes ONLY when the next BOLD+UNDERLINE header is encountered
//     (whether in config or not). Empty blocks, underline-only sub-section
//     dividers, and BOLD+ITALIC sub-headers within the body do NOT close
//     the group.
//   - Header blocks themselves are never processed.
//   - Blocks whose BOLD covers the entire text (all-caps decorative BOLD,
//     e.g. "DATA REVIEWED") are left alone.
//
// Per-row behavior: existing BOLD ranges on the row are dropped (single
// source of truth for the prefix), then a new BOLD range
// {offset:0, length:<chars before splitter>} is added. UNDERLINE/ITALIC
// ranges are preserved.
//
// Runs AFTER splitListItemNewlines so block-per-line is the unit of work.
// Mirrors backend DraftJsNormalizer::applyHeaderItemBoldPrefix.

// Per-section bolding rule. Map an exact header text → RegExp marking the
// prefix-end splitter. Use lookbehind `(?<=:)\s` to include the colon in
// the bolded prefix; use lookahead `(?=\s-\s)` to exclude the leading space
// of ` - ` separators.
const HEADER_ITEM_BOLD_SPLITTERS = {
  // ': ' (colon-space), or ' - ' / ' – ' / ' — ' (hyphen/en-dash/em-dash
  // surrounded by spaces).
  "PHYSICAL EXAMINATION": /(?:: | [-–—] )/u,
  PLAN: /(?:: | [-–—] )/u,
};

const hasStyle = (block, style) =>
  (block && block.inlineStyleRanges ? block.inlineStyleRanges : []).some(
    (s) => s && s.style === style
  );

const isSectionHeader = (block) =>
  block && block.text && block.text.length > 0 && hasStyle(block, "BOLD") && hasStyle(block, "UNDERLINE");

const isSubHeader = (block) =>
  block && block.text && block.text.length > 0 && hasStyle(block, "BOLD") && hasStyle(block, "ITALIC");

const applyBoldPrefix = (block, regex) => {
  const text = typeof (block && block.text) === "string" ? block.text : "";
  if (text === "") return block;

  const ranges = Array.isArray(block.inlineStyleRanges) ? block.inlineStyleRanges : [];

  // Preserve header-style decorative BOLD — all-caps text whose BOLD covers
  // (approximately) the whole text. Catches blocks like "DATA REVIEWED" that
  // lack UNDERLINE but are functionally headers. Content blocks with
  // full-text BOLD (mixed case) are still rewritten by the prefix rule.
  const textLen = text.length;
  if (text.toUpperCase() === text) {
    const hasFullTextBold = ranges.some(
      (r) => r && r.style === "BOLD" && (r.offset || 0) === 0 && (r.length || 0) >= textLen
    );
    if (hasFullTextBold) return block;
  }

  // Drop any existing BOLD — single source of truth for the prefix.
  const kept = ranges.filter((r) => r && r.style !== "BOLD");

  // Reset lastIndex in case caller passed a /g regex; we always want the
  // first match.
  regex.lastIndex = 0;
  const m = regex.exec(text);
  if (m && m.index > 0) {
    kept.push({ offset: 0, length: m.index, style: "BOLD" });
  }

  return { ...block, inlineStyleRanges: kept };
};

const applyHeaderItemBoldPrefix = (dataMix) => {
  if (!dataMix || !dataMix.blocks || !Array.isArray(dataMix.blocks)) return dataMix;
  if (Object.keys(HEADER_ITEM_BOLD_SPLITTERS).length === 0) return dataMix;

  const blocks = [...dataMix.blocks];
  let activeSplitter = null;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    // Real headers are strictly BOLD+UNDERLINE — they open/close groups.
    // BOLD+ITALIC sub-headers are structural dividers inside the section
    // body, not group boundaries.
    if (isSectionHeader(block)) {
      const headerText = (typeof block?.text === "string" ? block.text : "").trim();
      activeSplitter = HEADER_ITEM_BOLD_SPLITTERS[headerText] || null;
      continue;
    }

    if (activeSplitter === null) continue;

    const text = typeof block?.text === "string" ? block.text : "";
    const isEmpty = text === "" || text.replaceAll(" ", "") === "";
    if (isEmpty) continue;

    // Sub-headers (BOLD+ITALIC, e.g. <em><strong>Antiplatelet Therapy</strong></em>)
    // are decorative dividers inside the section body — skip processing so
    // their BOLD stays intact.
    if (isSubHeader(block)) continue;

    blocks[i] = applyBoldPrefix(block, activeSplitter);
  }

  dataMix.blocks = blocks;
  return dataMix;
};

export default applyHeaderItemBoldPrefix;
